import { useEffect, useMemo, useState } from 'react';

import { Button, Divider, Input, Select, Text, TypeSelectOption, dialogService } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { SearchFilter, Table, TableBottomField, TableTopField } from '~/components/common';

import { useStableCallback } from '~/hooks/common';
import { useGetVendorReleaseInventory, useOption } from '~/hooks/stock';
import { useStockReleaseStore } from '~/store';
import { getStockReleaseRegisterInventoryColumnDefs } from '~/utils';

import {
  INITIAL_STOCK_PAGE_OFFSET,
  INITIAL_STOCK_PAGE_SIZE,
  MAX_STOCK_RELEASE_REQUEST_PRODUCT_COUNT,
  STOCK_CODES,
  STOCK_PAGE_SIZE,
} from '~/constants';

import { TypeStockReleaseGoods, TypeStockReleaseInventoryFilter } from '~/types/stock.types';

import styles from './StockReleaseRegisterFirstStepContent.module.scss';

const INVENTORY_TABLE_HEIGHT = 399;

const INITIAL_STOCK_RELEASE_REGISTER_FILTER: TypeStockReleaseInventoryFilter = {
  /** 반출 출발지 센터 */
  cluster: null,
  /** 검색 키워드 유형 */
  keywordType: null,
  /** 검색 키워드 값 */
  keywordValue: '',
  /** 페이지 사이즈 */
  pageSize: INITIAL_STOCK_PAGE_SIZE,
  /** 페이지 번호 */
  currentPage: INITIAL_STOCK_PAGE_OFFSET,
};

/**
 * [재고관리 > 반출요청관리] 반출요청등록 모달 > STEP 1 > 반출 요청 상품 검색
 */
const StockReleaseRegisterFirstStepSearch = () => {
  const { cluster, requestGoods, setRequestGoods, setCluster, resetRequestGoods } = useStockReleaseStore(
    useShallow((state) => ({
      cluster: state.cluster,
      requestGoods: state.requestGoods,
      setRequestGoods: state.actions.setRequestGoods,
      setCluster: state.actions.setCluster,
      resetRequestGoods: state.actions.resetRequestGoods,
    }))
  );

  const { options } = useOption({
    codes: [STOCK_CODES.vendorReleaseCluster, STOCK_CODES.vendorReleaseInventoryKeyword],
  });

  const { getRowId: getInventoryRowId, columnDefs: inventoryColumnDefs } = useMemo(
    () => getStockReleaseRegisterInventoryColumnDefs(),
    []
  );
  const { requestVendorReleaseInventory, responseData, resetResponseData } = useGetVendorReleaseInventory();

  const [filter, setFilter] = useState<TypeStockReleaseInventoryFilter>(INITIAL_STOCK_RELEASE_REGISTER_FILTER);
  const [selectedItems, setSelectedItems] = useState<TypeStockReleaseGoods[]>([]);
  const selectedRowIds = useMemo(() => selectedItems.map(getInventoryRowId), [selectedItems, getInventoryRowId]);

  // 새 조회 결과가 오면 선택을 비운다
  useEffect(() => {
    setSelectedItems([]);
  }, [responseData]);

  // 검색 필터 값 변경
  const handleChangeFilterValues = (values: Partial<TypeStockReleaseInventoryFilter>) => {
    // 반출 출발지 센터 변경 시, 이미 추가된 반출 상품이 있는 경우 변경 불가
    const isClusterChanging = values.cluster !== undefined && values.cluster !== filter.cluster;
    if (requestGoods.length > 0 && isClusterChanging) {
      dialogService.addDialog({
        type: 'confirm',
        children:
          '반출 출발지 센터를 변경하면 반출 상품을 처음부터 다시 추가해야 합니다.\n반출 출발지 센터를 변경하시겠습니까?',
        onConfirm: () => {
          resetResponseData();
          resetRequestGoods();
          setFilter((prev) => ({ ...prev, ...values }));
        },
      });
      return;
    }

    setFilter((prev) => ({ ...prev, ...values }));
  };

  // 검색 된 상품 선택 핸들러
  const handleSelectionChangeSearchItems = (selectedRows: TypeStockReleaseGoods[]) => {
    setSelectedItems(selectedRows);
  };

  // 검색 실행
  const requestSearch = (filterValues: Partial<TypeStockReleaseInventoryFilter> = {}) => {
    const filterState = { ...filter, ...filterValues };
    requestVendorReleaseInventory(filterState, {
      onSuccess: () => {
        setFilter(filterState);
        if (filterState.cluster) setCluster(filterState.cluster);
      },
    });
  };

  // 페이지 사이즈 변경
  const handleChangePageLimit = (option: TypeSelectOption<number>) => {
    requestSearch({ pageSize: option.value, currentPage: INITIAL_STOCK_PAGE_OFFSET });
  };

  // 페이지네이션 변경
  const handleClickPage = (currentPage: number) => {
    requestSearch({ currentPage });
  };

  // 필터 검색 버튼 클릭
  const handleClickSearch = () => {
    requestSearch({ currentPage: INITIAL_STOCK_PAGE_OFFSET });
  };

  // 필터 초기화 버튼 클릭
  const handleClickFilterReset = () => {
    setFilter({
      ...INITIAL_STOCK_RELEASE_REGISTER_FILTER,
      ...(options?.vendorReleaseCluster?.length && { cluster: options.vendorReleaseCluster[0] }),
      ...(options?.vendorReleaseInventoryKeyword?.length && { keywordType: options.vendorReleaseInventoryKeyword[0] }),
    });
  };

  // 반출 상품 추가 버튼 클릭
  const handleClickAddGoods = () => {
    if (requestGoods.length + selectedItems.length > MAX_STOCK_RELEASE_REQUEST_PRODUCT_COUNT) {
      dialogService.addDialog({
        children: `최대 ${MAX_STOCK_RELEASE_REQUEST_PRODUCT_COUNT}개 SKU 까지 반출 요청할 수 있습니다.`,
      });
      return;
    }

    setRequestGoods(selectedItems);
    setSelectedItems([]);
  };

  const setDefaultFilter = useStableCallback(() => {
    if (options?.vendorReleaseCluster && options?.vendorReleaseInventoryKeyword) {
      setFilter((prev) => ({
        ...prev,
        cluster: cluster ?? options.vendorReleaseCluster?.[0] ?? null,
        keywordType: options.vendorReleaseInventoryKeyword?.[0] ?? null,
      }));
    }
  });

  useEffect(() => {
    setDefaultFilter();
  }, [options?.vendorReleaseCluster, options?.vendorReleaseInventoryKeyword, setDefaultFilter]);

  return (
    <div className={styles['search-section']}>
      <SearchFilter
        formId="stock-release-inventory-search-form"
        onSubmit={handleClickSearch}
        onReset={handleClickFilterReset}
      >
        <div className={styles['filters-container']}>
          <div className={styles['filter-wrapper']}>
            <Text className={styles['filter-label']} size="small" weight="semi-bold">
              반출 출발지 센터
            </Text>
            <Select
              className={styles['input-select']}
              size="small"
              options={options?.vendorReleaseCluster ?? []}
              value={filter.cluster}
              onChange={(_: unknown, cluster: TypeSelectOption<string>) => handleChangeFilterValues({ cluster })}
            />
          </div>
          <div className={`${styles['filter-wrapper']} ${styles['select-input-field']}`}>
            <Text className={styles['filter-label']} size="small" weight="semi-bold">
              키워드
            </Text>
            <div className={styles['select-input-container']}>
              <Select
                className={styles['input-select']}
                id="keywordType"
                size="small"
                options={options?.vendorReleaseInventoryKeyword ?? []}
                value={filter.keywordType}
                onChange={(_: unknown, keywordType: TypeSelectOption<string>) =>
                  handleChangeFilterValues({ keywordType, keywordValue: '' })
                }
              />
              <Input
                type="text"
                id="keywordValue"
                size="small"
                placeholder="입력"
                value={filter.keywordValue}
                onChange={(e) => handleChangeFilterValues({ keywordValue: e.target.value })}
                onBlur={(e) => handleChangeFilterValues({ keywordValue: e.target.value.trim() })}
              />
            </div>
          </div>
        </div>
      </SearchFilter>
      <Divider color="gray100" />
      <div className={`${styles['table-section']} ${styles['search-items-table']}`}>
        <TableTopField
          dataTotalCount={responseData?.pagination?.listTotal ?? 0}
          selectedCount={selectedItems.length}
          rightButton={
            <div className={styles['table-top-right-field']}>
              <Button
                size="tiny"
                color="secondary"
                disabled={selectedItems.length === 0 || requestGoods.length > MAX_STOCK_RELEASE_REQUEST_PRODUCT_COUNT}
                onClick={handleClickAddGoods}
              >
                반출 상품 추가
              </Button>
            </div>
          }
        />
        <div className={styles['table-wrapper']}>
          <Table
            className={styles['table']}
            data={responseData?.data ?? []}
            columns={inventoryColumnDefs}
            getRowId={getInventoryRowId}
            height={INVENTORY_TABLE_HEIGHT}
            rowStatusType={!responseData?.data ? 'STOCK_RELEASE_REGISTER' : 'EMPTY'}
            selection={{ selectedRowIds, onChange: handleSelectionChangeSearchItems }}
          />
        </div>
        <TableBottomField
          pageSizeOptions={Object.values(STOCK_PAGE_SIZE)}
          currentPage={filter.currentPage}
          totalCount={responseData?.pagination?.listTotal ?? 0}
          pageSize={filter.pageSize}
          onChangePageLimit={handleChangePageLimit}
          onClickPage={handleClickPage}
        />
      </div>
    </div>
  );
};
export default StockReleaseRegisterFirstStepSearch;
